import { z } from "zod";
import { queryOptions, useQuery } from "@tanstack/react-query";

const accountSchema = z.object({
  id: z.number(),
  picture: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  domain: z.string().nullable().optional(),
});

const ownerSchema = z.object({
  id: z.number(),
  picture: z.string().nullable().optional(),
  name: z.string(),
  email: z.string(),
});

const contactSchema = z.object({
  id: z.number(),
  prefix: z.string().nullable().optional(),
  firstname: z.string().nullable().optional(),
  middle: z.string().nullable().optional(),
  lastname: z.string().nullable().optional(),
  suffix: z.string().nullable().optional(),
  name: z.string().nullable().optional(),
  email: z.string().nullable().optional(),
  domain: z.string().nullable().optional(),
  picture: z.string().nullable().optional(),
  creation_date: z.string(), // e.g., "2024-12-21T15:35:29.000Z"
  modification_date: z.string(), // e.g., "2025-02-04T21:21:54.000Z"
  archived: z.boolean(),
  not_viewed: z.boolean().optional(),
  account: accountSchema.nullable().optional(),
  owner: ownerSchema.nullable().optional(),
  connection_strength: z.number().optional(),
  last_email_date: z.string().nullable().optional(),
  last_meeting_date: z.string().nullable().optional(),
  social_profiles: z.array(z.unknown()).optional(),
  phone_numbers: z.array(z.any()).optional(), // or define more precisely
  positions: z.array(z.unknown()).optional(),
  addresses: z.array(z.unknown()).optional(),
  tags: z.array(z.unknown()).optional(),
  custom: z.unknown().nullable().optional(),
  can_edit: z.boolean().optional(),
  organisation: z.unknown().nullable().optional(),
  role: z.unknown().nullable().optional(),
  "opt-out": z.boolean().optional(),
  bounced: z.boolean().optional(),
  phone_number: z.string().nullable().optional(),
  mobile_phone_number: z.string().nullable().optional(),
  home_phone_number: z.string().nullable().optional(),
  fax_number: z.string().nullable().optional(),
});

export type Contact = z.infer<typeof contactSchema>;

/**
 * GET all contacts from the server
 */
const fetchContacts = async () => {
  const response = await fetch(
    "https://api.salesflare.com/contacts?limit=1000",
    {
      headers: {
        Authorization: `Bearer ${import.meta.env.VITE_SALESFLARE_API_KEY}`,
      },
    },
  );
  return response.json();
};

export const contactsQueryOptions = queryOptions({
  queryKey: ["contacts"],
  queryFn: fetchContacts,
  staleTime: 1000 * 60 * 2, // 2 minutes
});

export function useContacts() {
  return useQuery(contactsQueryOptions);
}
